import './index.scss';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-semantic-toasts';
import { Form, Segment, Button } from 'semantic-ui/semantic-ui-imports';
import LanguageFormSelect from 'components/LanguageSelector/LanguageFormSelect';
import { useAuth } from '../../contexts/auth';
import CountrySelector from '../../components/CountrySelector';
import axios from '../../services/request';
import { User } from '../../Types/User';

interface ErrorFields {
  id?: string[];
  first_name?: string[];
  last_name?: string[];
  address?: string[];
  postal_code?: string[];
  country_code?: string[];
  tel?: string[];
  company?: string[];
  vat_no?: string[];
  city?: string[];
  preferred_language?: string[];
}

const Profile = () => {
  const { getUser } = useAuth();
  const { t } = useTranslation();
  const [user, setUser] = React.useState<User | any>();
  const [error, setError] = React.useState<ErrorFields | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await axios.put(`/users/${user.id}/`, {
        ...user,
      });
      if (response && response.status === 200) {
        setError(null);
        getUser();
      } else {
        setError(response.data.error);
      }
      setIsLoading(false);
      toast({
        type: 'success',
        title: `${t('profile.form.success')}   `,
        time: 4000,
        size: 'small',
      });
      return response;
    } catch (err: any) {
      setIsLoading(false);
      setError(err.response.data);

      return err;
    }
  };

  const fetchUser = async () => {
    const u = await getUser();
    if (u) {
      setUser(u);
    }
  };

  React.useEffect(() => {
    fetchUser();
  }, []);

  return (
    <div className="profile-page">
      <h2>{t('profile.title')}</h2>

      <Form onSubmit={onSubmit}>
        <Segment className="profile-page__form">
          <Form.Input
            name="first_name"
            label={t('profile.form.first_name')}
            value={user?.first_name || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, first_name: value })
            }
            error={error?.first_name}
          />
          <Form.Input
            name="last_name"
            label={t('profile.form.last_name')}
            value={user?.last_name || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, last_name: value })
            }
            error={error?.last_name}
          />
          <Form.Input
            name="address"
            label={t('profile.form.address')}
            value={user?.address || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, address: value })
            }
            error={error?.address}
          />
          <Form.Input
            name="city"
            label={t('profile.form.city')}
            value={user?.city || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, city: value })
            }
            error={error?.city}
          />
          <Form.Input
            name="postal_code"
            label={t('profile.form.postal_code')}
            value={user?.postal_code || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, postal_code: value })
            }
            error={error?.postal_code}
          />
          <CountrySelector
            selected={user?.country_code || ''}
            placeholder={t('profile.form.country_code')}
            onSelect={(code: string) =>
              setUser({ ...user, country_code: code })
            }
            isSearchable={true}
            label={t('registerPage.countryCode')}
            error={error?.country_code}
          />
          <LanguageFormSelect
            onSelect={(code: string) =>
              setUser({ ...user, preferred_language: code })
            }
            label={t('profile.form.languageCode')}
            placeholder={t('profile.form.languageCode')}
            selected={user?.preferred_language || ''}
            error={error?.preferred_language}
          />
          <Form.Input
            name="tel"
            label={t('profile.form.tel')}
            value={user?.tel || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, tel: value })
            }
            error={error?.tel}
          />
          <Form.Input
            name="company"
            label={t('profile.form.company')}
            value={user?.company || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, company: value })
            }
            error={error?.company}
          />
          <Form.Input
            name="vat_no"
            label={t('profile.form.vat_no')}
            value={user?.vat_no || ''}
            onChange={(e: any, { value }: any) =>
              setUser({ ...user, vat_no: value })
            }
            error={error?.vat_no}
          />
          <Button
            loading={isLoading}
            color="teal"
            fluid
            size="large"
            type="submit"
          >
            {t('profile.form.submit')}
          </Button>
        </Segment>
      </Form>
    </div>
  );
};

export default Profile;
